import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { Typography, Switch, Space } from "antd";
import { useState } from "react";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";

import { formatMoneyAmount } from "../../lib/formats/money";
import { tableAddEditDiscardColumn } from "../../lib/formats/tables";
import {
  type ActivityDetailsFragment,
  type MachineCostFragment,
  type MachineCostFields,
  type MachineCostChanges,
  UnitType,
  type MachineDetailsFragment,
  type MachineCostCreateMutation,
  type MachineCostUpdateMutation,
} from "../../lib/graphql";
import {
  useMachineCostCreate,
  useMachineCostDiscard,
  useMachineCostUpdate,
} from "../../lib/hooks/agro/machineCosts";
import { useMachineKindOptions } from "../../lib/hooks/basic/machineKinds";
import { useMachineOptions } from "../../lib/hooks/basic/machines";
import { useCurrentUser } from "../../lib/hooks/basic/users";
import { filterColumns, filterFalse } from "../../lib/utils";
import { Form, Rules } from "../form/Form";
import { InputNumber } from "../form/InputNumber";
import { ItemSidebar } from "../shared/ItemSidebar";
import { useItemSidebarContext } from "../shared/ItemSidebar/Provider";
import { DiscardItem } from "../shared/ListView/DiscardItem";
import { EditForm } from "../shared/ListView/EditForm";
import type { ItemFieldFormConfig } from "../shared/ListView/ItemForm";
import { NewForm } from "../shared/ListView/NewForm";
import { Table } from "../shared/Table";
import { UnitSelect } from "../units/UnitSelect";

const messages = defineMessages({
  machineCost: {
    id: "activities.machineCost",
    defaultMessage: "machineCost",
  },
  allMachineKinds: {
    id: "machineKinds.all",
    defaultMessage: "allMachineKinds",
  },
  allMachines: { id: "machines.all", defaultMessage: "allMachines" },
});

interface MachineCostsProps {
  activity?: ActivityDetailsFragment;
  machine?: MachineDetailsFragment;
  readonly?: boolean;
}

function MachineCostTable({
  machineCosts,
  setCurrentMachineCost,
  readonly,
  context,
}: {
  machineCosts: MachineCostFragment[];
  setCurrentMachineCost: (cost: MachineCostFragment) => void;
  readonly?: boolean;
  context: "activity" | "machine";
}) {
  const [showAll, setShowAll] = useState(false);
  const { setCurrentAction } = useItemSidebarContext();
  const { currentTenant } = useCurrentUser();

  const machineCostsData = machineCosts.filter(
    (p) => showAll || !p.discardedAt
  );

  return (
    <div style={{ position: "relative", textAlign: "right" }}>
      <Space style={{ marginBottom: "4px" }}>
        <FormattedMessage id="showDiscarded" />
        <Switch
          unCheckedChildren={<EyeInvisibleOutlined />}
          checkedChildren={<EyeOutlined />}
          onChange={(checked) => setShowAll(checked)}
        />
      </Space>

      <Table
        dataSource={machineCostsData}
        pagination={false}
        rowKey="id"
        rowClassName={(v) => (v.discardedAt ? "row-disabled" : "")}
        columns={filterColumns([
          {
            title: <FormattedMessage id="cost" defaultMessage="cost" />,
            dataIndex: "cost",
            render: (cost, p) => (
              <>
                {formatMoneyAmount(cost, currentTenant.currencyCode)}
                {" / "}
                {p.unit.abbr}
              </>
            ),
          },
          context === "activity" && {
            title: <FormattedMessage id="machineKinds.entityName" />,
            dataIndex: ["machineKind", "name"],
            render: (name: string) =>
              name || (
                <FormattedMessage
                  id="machineKinds.all"
                  defaultMessage="allMachineKinds"
                />
              ),
          },
          context === "activity" && {
            title: <FormattedMessage id="machines.entityName" />,
            dataIndex: ["machine", "name"],
            render: (name: string) =>
              name || (
                <FormattedMessage
                  id="machines.all"
                  defaultMessage="allMachines"
                />
              ),
          },
          ...(readonly
            ? []
            : [
                tableAddEditDiscardColumn<MachineCostFragment>({
                  onNew: () => {
                    setCurrentMachineCost({} as MachineCostFragment);
                    setCurrentAction("newMachineCost");
                  },
                  onEdit: (p) => {
                    setCurrentMachineCost(p);
                    setCurrentAction("editMachineCost");
                  },
                  onDiscard: (p) => {
                    setCurrentMachineCost(p);
                    setCurrentAction("discardMachineCost");
                  },
                }),
              ]),
        ])}
      />
    </div>
  );
}

export function MachineCosts({
  activity,
  machine,
  readonly,
}: MachineCostsProps) {
  const intl = useIntl();
  const entityName = intl.formatMessage(messages.machineCost);
  const [currentMachineCost, setCurrentMachineCost] =
    useState<MachineCostFragment>();
  const { currentTenant } = useCurrentUser();
  const context = activity ? "activity" : "machine";
  const refetchQueries = [activity ? "Activity" : "Machine"];

  if (!currentTenant) return null;

  const fields: ItemFieldFormConfig<MachineCostFields | MachineCostChanges>[] =
    filterFalse([
      {
        key: "cost",
        type: "custom",
        render: () => (
          <Form.Item
            key="cost"
            name="cost"
            rules={[Rules.gtZero]}
            label={<FormattedMessage id="cost" />}
          >
            <InputNumber
              addonBefore={currentTenant.currency.symbol}
              per={
                <Form.Item name="unitId" rules={[Rules.required]} noStyle>
                  <UnitSelect
                    labelRenderer={(value) => (
                      <FormattedMessage id="per" values={{ value }} />
                    )}
                    showGroups
                    unitTypes={[UnitType.Area, UnitType.Time]}
                  />
                </Form.Item>
              }
            />
          </Form.Item>
        ),
      },
      context === "activity" && {
        label: <FormattedMessage id="machineKinds.entityName" />,
        name: "machineKindId" as any,
        type: "select" as const,
        showGroups: true,
        placeholder: intl.formatMessage(messages.allMachineKinds),
        optionsHook: useMachineKindOptions,
        onChange: (_: any, _item: any, _opt: any, form: any) => {
          form.setFieldValue("machineId", null);
        },
        disabled: !!machine,
      },
      context === "activity" && {
        label: <FormattedMessage id="machines.entityName" />,
        name: "machineId" as any,
        type: "select" as const,
        placeholder: intl.formatMessage(messages.allMachines),
        showGroups: true,
        optionsHook: useMachineOptions,
        onChange: (_: any, _item: any, _opt: any, form: any) => {
          form.setFieldValue("machineKindId", null);
        },
      },
    ]);

  return (
    <>
      <Typography.Paragraph type="secondary">
        <FormattedMessage
          id="activities.machineCost.sectionHint"
          defaultMessage="sectionHint"
        />
      </Typography.Paragraph>

      <ItemSidebar
        item={currentMachineCost}
        sidebarActions={{
          editMachineCost: ({ item, closeSidebar }) =>
            item && (
              <EditForm
                item={item}
                entityName={entityName}
                mutation={useMachineCostUpdate}
                mutationOptions={{ refetchQueries }}
                onMutate={(result) =>
                  (result as MachineCostUpdateMutation).machineCostUpdate
                }
                onSuccess={closeSidebar}
                onCancel={closeSidebar}
                fields={fields}
                formValues={(p) => ({
                  cost: p.cost,
                  unitId: p.unit.id,
                  machineKindId: p.machineKind?.id,
                  machineId: p.machine?.id,
                })}
              />
            ),
          newMachineCost: ({ item, closeSidebar }) =>
            item && (
              <NewForm
                entityName={entityName}
                mutation={useMachineCostCreate}
                mutationOptions={{ refetchQueries }}
                onMutate={(result) =>
                  (result as MachineCostCreateMutation).machineCostCreate
                }
                onClose={closeSidebar}
                fields={fields}
                formValues={() =>
                  ({
                    activityId: activity?.id,
                    machineId: machine?.id,
                    unitId: activity?.progressUnit?.id,
                  }) as MachineCostFields
                }
              />
            ),
          discardMachineCost: ({ item, closeSidebar }) =>
            item && (
              <DiscardItem
                item={item}
                entityName={entityName}
                mutation={useMachineCostDiscard}
                onMutate={(result) => result.machineCostDiscard}
                mutationOptions={{ refetchQueries }}
                onCancel={closeSidebar}
                onSuccess={closeSidebar}
              />
            ),
        }}
      />

      <br />
      <MachineCostTable
        machineCosts={
          activity?.machineCosts ||
          machine?.machineCosts.filter((c) => !c.activity) ||
          []
        }
        setCurrentMachineCost={setCurrentMachineCost}
        readonly={readonly}
        context={context}
      />
    </>
  );
}
